/* ============================================================
   MCH — SIGNAL · motion engine
   WebGL thin-film field · reveals · marquee · rails · counters
   ============================================================ */
(() => {
  'use strict';

  const doc = document.documentElement;
  // boot marker: tells CSS the motion engine is alive, so the no-JS
  // safety net (rv-net) stays out of the way of real scroll reveals
  doc.classList.add('js-live');
  // merchant decision 2026-07-17: animate regardless of OS reduce-motion setting
  const REDUCED = false;
  const FINE = matchMedia('(pointer: fine)').matches;

  // one broken feature must never take the rest of the page down with it
  const safe = (name, fn) => { try { fn(); } catch (err) { console.warn('[signal]', name, err); } };

  /* ---------------- WebGL signal field ---------------- */
  // Thin-film interference ribbons drifting across paper. One quad, one
  // fragment shader; DPR-capped, paused when offscreen or tab-hidden.
  function initField(host) {
    const canvas = document.createElement('canvas');
    host.appendChild(canvas);
    const gl = canvas.getContext('webgl', { antialias: false, alpha: true, powerPreference: 'low-power' });
    if (!gl) { host.classList.add('is-fallback'); return; }

    const VS = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
    const FS = `
precision highp float;
uniform vec2 uRes; uniform float uT; uniform vec2 uMouse; uniform float uMouseAmp;
// hash + value noise
float h(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3. - 2. * f);
  return mix(mix(h(i), h(i + vec2(1., 0.)), u.x), mix(h(i + vec2(0., 1.)), h(i + vec2(1., 1.)), u.x), u.y);
}
float fbm(vec2 p){
  float v = 0., a = .55;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = p * 2.05 + 11.3; a *= .5; }
  return v;
}
// thin-film-ish palette: paper -> chrome pastels
vec3 film(float t){
  vec3 a = vec3(.957, .949, .918);              // paper
  vec3 b = vec3(.965, .835, .576);              // champagne
  vec3 c = vec3(1., .788, .627);                // apricot
  vec3 d = vec3(.949, .769, .769);              // rose
  vec3 e = vec3(1., .914, .753);                // pale gold
  float x = fract(t);
  if (x < .25) return mix(a, b, smoothstep(0., .25, x) );
  if (x < .5)  return mix(b, c, smoothstep(.25, .5, x));
  if (x < .75) return mix(c, d, smoothstep(.5, .75, x));
  return mix(d, e, smoothstep(.75, 1., x));
}
void main(){
  vec2 uv = gl_FragCoord.xy / uRes.xy;
  vec2 p = uv; p.x *= uRes.x / uRes.y;
  float t = uT * .05;

  // mouse ripple
  vec2 m = uMouse; m.x *= uRes.x / uRes.y;
  float md = length(p - m);
  float ripple = uMouseAmp * .35 * exp(-md * 3.) * sin(md * 22. - uT * 2.4);

  // flowing ridge field
  vec2 q = vec2(fbm(p * 1.35 + vec2(t * .9, -t * .6)), fbm(p * 1.35 + vec2(-t * .5, t * .8) + 4.7));
  float r = fbm(p * 1.9 + q * 1.6 + ripple);
  float ridge = smoothstep(.32, .5, r) * (1. - smoothstep(.5, .68, r)); // band
  float ridge2 = smoothstep(.45, .62, r) * (1. - smoothstep(.62, .8, r));

  vec3 col = vec3(.957, .949, .918); // paper base
  vec3 chrome = film(r * 1.9 + t * .7 + q.x * .8);
  col = mix(col, chrome, ridge * .9);
  col = mix(col, film(r * 2.4 + .35 + t), ridge2 * .55);

  // faint ultramarine vein along the sharpest ridges
  float vein = smoothstep(.49, .5, r) * (1. - smoothstep(.5, .515, r));
  col = mix(col, vec3(.557, .122, .133), vein * .6);

  // vignette to keep edges papery
  float vig = smoothstep(1.25, .45, length(uv - .5));
  col = mix(vec3(.957, .949, .918), col, vig);

  gl_FragColor = vec4(col, 1.);
}`;

    function sh(type, src) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    }
    const vs = sh(gl.VERTEX_SHADER, VS), fs = sh(gl.FRAGMENT_SHADER, FS);
    if (!vs || !fs) { host.classList.add('is-fallback'); return; }
    const prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const uRes = gl.getUniformLocation(prog, 'uRes');
    const uT = gl.getUniformLocation(prog, 'uT');
    const uMouse = gl.getUniformLocation(prog, 'uMouse');
    const uMouseAmp = gl.getUniformLocation(prog, 'uMouseAmp');

    let w = 0, hpx = 0, ready = false;
    function size() {
      const dpr = Math.min(devicePixelRatio || 1, 1.5);
      const r = host.getBoundingClientRect();
      w = Math.max(2, Math.round(r.width * dpr));
      hpx = Math.max(2, Math.round(r.height * dpr));
      canvas.width = w; canvas.height = hpx;
      gl.viewport(0, 0, w, hpx);
      // setting canvas.width wipes the buffer; repaint immediately so a
      // paused loop (hidden tab, reduced motion) never leaves it blank
      if (ready) frame(performance.now());
    }
    size();
    addEventListener('resize', size);

    let mx = .5, my = .5, tx = .5, ty = .5, amp = 0, tAmp = 0;
    if (FINE) {
      host.ownerDocument.addEventListener('pointermove', (e) => {
        const r = host.getBoundingClientRect();
        if (e.clientY < r.top || e.clientY > r.bottom) { tAmp = 0; return; }
        tx = (e.clientX - r.left) / r.width;
        ty = 1 - (e.clientY - r.top) / r.height;
        tAmp = 1;
      }, { passive: true });
    }

    let visible = true, raf = 0;
    const io = new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      if (visible && !raf && !REDUCED) loop();
    });
    io.observe(host);

    const t0 = performance.now();
    function frame(now) {
      mx += (tx - mx) * .06; my += (ty - my) * .06; amp += (tAmp - amp) * .04;
      gl.uniform2f(uRes, w, hpx);
      gl.uniform1f(uT, (now - t0) / 1000);
      gl.uniform2f(uMouse, mx * (w / hpx), my);
      gl.uniform1f(uMouseAmp, amp);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    function loop() {
      raf = requestAnimationFrame((now) => {
        raf = 0;
        if (!visible || document.hidden) return;
        frame(now);
        loop();
      });
    }
    ready = true;
    frame(t0);              // always paint one frame (reduced motion gets a still)
    if (!REDUCED) loop();
    document.addEventListener('visibilitychange', () => { if (!document.hidden && visible && !raf && !REDUCED) loop(); });
  }
  safe('field', () => document.querySelectorAll('[data-signal-field]').forEach(initField));

  /* ---------------- fit-to-width display text ---------------- */
  function fit(el) {
    const max = parseFloat(el.dataset.fitMax || '1000');
    // measure at max-content width — a block element's scrollWidth equals its
    // own clientWidth when nothing overflows, which defeats the ratio math
    const prevDisplay = el.style.display;
    const prevWs = el.style.whiteSpace;
    el.style.display = 'inline-block';
    el.style.whiteSpace = 'nowrap';
    el.style.fontSize = '100px';
    const w = el.getBoundingClientRect().width;
    const target = el.parentElement.clientWidth;
    el.style.display = prevDisplay;
    el.style.whiteSpace = prevWs;
    if (w && target) {
      el.style.fontSize = Math.min(max, 100 * target / w * .995) + 'px';
    } else {
      el.style.fontSize = '';   // unmeasurable (offscreen/contained): fall back to CSS size
    }
    el.classList.add('is-fit'); // never leave fitted text invisible
  }
  const fits = [...document.querySelectorAll('[data-fitwidth]')];
  function fitAll() { fits.forEach((el) => safe('fit-el', () => fit(el))); }
  // fit immediately (fallback metrics — never show raw clamp size), then
  // re-fit whenever webfonts actually arrive: with font-display swap on a
  // slow connection Unbounded lands seconds later and is much WIDER than
  // the fallback face, so a fit measured pre-swap overflows and clips.
  // fonts.ready + loadingdone are the reliable signals on WebKit — a
  // fonts.load('800 100px Unbounded') promise is not (variable-font quirks).
  safe('fit', () => {
    if (fits.length === 0) return;
    fitAll();
    if (document.fonts) {
      if (document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(() => fitAll()).catch(() => {});
      }
      if (document.fonts.addEventListener) {
        document.fonts.addEventListener('loadingdone', () => fitAll());
      }
    }
    let lastW = innerWidth;
    let rT = 0;
    addEventListener('resize', () => {
      if (Math.abs(innerWidth - lastW) < 2) return;   // iOS URL-bar height wobble
      lastW = innerWidth;
      clearTimeout(rT);
      rT = setTimeout(fitAll, 120);
    });
  });

  /* ---------------- reveals ---------------- */
  safe('reveals', () => {
    const ro = new IntersectionObserver((ens) => {
      ens.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); ro.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    document.querySelectorAll('[data-reveal], [data-reveal-child]').forEach((el) => ro.observe(el));
  });

  /* ---------------- counters ---------------- */
  const co = new IntersectionObserver((ens) => {
    ens.forEach((en) => {
      if (!en.isIntersecting) return;
      co.unobserve(en.target);
      const el = en.target;
      const end = parseFloat(el.dataset.countup);
      const dur = 1600;
      const t0 = performance.now();
      const fmt = (v) => {
        const s = String(Math.round(v));
        return s.length > 3 ? s.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : s;
      };
      if (REDUCED) { el.textContent = fmt(end); return; }
      (function tick(now) {
        const k = Math.min(1, (now - t0) / dur);
        el.textContent = fmt(end * (1 - Math.pow(1 - k, 4)));
        if (k < 1) requestAnimationFrame(tick);
      })(t0);
    });
  }, { threshold: .5 });
  safe('countup', () => document.querySelectorAll('[data-countup]').forEach((el) => co.observe(el)));

  /* ---------------- magnetic ---------------- */
  if (FINE && !REDUCED) safe('magnetic', () => {
    document.querySelectorAll('[data-magnetic]').forEach((el) => {
      const K = parseFloat(el.dataset.magnetic || '.25');
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.translate = `${(e.clientX - r.left - r.width / 2) * K}px ${(e.clientY - r.top - r.height / 2) * K}px`;
      });
      el.addEventListener('pointerleave', () => { el.style.translate = ''; });
    });
  });

  /* ---------------- lazy model-viewer: 900KB module only when a viewer nears ---------------- */
  safe('mv-lazy', () => {
    const viewers = document.querySelectorAll('model-viewer');
    if (!viewers.length) return;
    let injected = false;
    const inject = () => {
      if (injected) return;
      injected = true;
      const src = document.body.dataset.mvSrc;
      if (!src) return;
      const sc = document.createElement('script');
      sc.type = 'module';
      sc.src = src;
      document.head.appendChild(sc);
    };
    const mo = new IntersectionObserver((ens) => {
      if (ens.some((e) => e.isIntersecting)) { inject(); mo.disconnect(); }
    }, { rootMargin: '600px 0px' });
    viewers.forEach((v) => mo.observe(v));
  });

  /* ---------------- same-page anchor scroll (works without CSS smooth) ---------------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const el = document.getElementById(a.getAttribute('href').slice(1));
    if (!el) return;
    e.preventDefault();
    history.pushState(null, '', a.getAttribute('href'));
    el.scrollIntoView({ behavior: REDUCED ? 'instant' : 'smooth', block: 'start' });
  });

  /* ---------------- header state ---------------- */
  const hdr = document.querySelector('.hdr');
  if (hdr) {
    const onScroll = () => hdr.classList.toggle('is-stuck', scrollY > 8);
    onScroll();
    addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------- overlays (menu / search) ---------------- */
  function overlay(root, openers, focusSel) {
    if (!root) return { open: () => {}, close: () => {} };
    let lastFocus = null;
    const open = () => {
      lastFocus = document.activeElement;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      const f = focusSel && root.querySelector(focusSel);
      setTimeout(() => f && f.focus(), 120);
    };
    const close = () => {
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      if (lastFocus) lastFocus.focus();
    };
    openers.forEach((b) => b && b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-overlay-close]') || e.target.classList.contains('menu__scrim') || e.target.classList.contains('search__scrim')) close();
    });
    addEventListener('keydown', (e) => { if (e.key === 'Escape' && root.classList.contains('is-open')) close(); });
    return { open, close };
  }
  overlay(document.querySelector('.js-menu'), [...document.querySelectorAll('[data-menu-open]')]);
  overlay(document.querySelector('.js-search'), [...document.querySelectorAll('[data-search-open]')], '.search__input');

  /* ---------------- drag-scroll rails ---------------- */
  safe('rails', () => document.querySelectorAll('[data-rail]').forEach((rail) => {
    const prev = document.querySelector(`[data-rail-prev="${rail.dataset.rail}"]`);
    const next = document.querySelector(`[data-rail-next="${rail.dataset.rail}"]`);
    const step = () => Math.max(rail.firstElementChild ? rail.firstElementChild.getBoundingClientRect().width + 18 : 320, 280);
    prev && prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    const sync = () => {
      if (prev) prev.disabled = rail.scrollLeft < 10;
      if (next) next.disabled = rail.scrollLeft > rail.scrollWidth - rail.clientWidth - 10;
    };
    rail.addEventListener('scroll', sync, { passive: true });
    addEventListener('resize', sync);
    sync();

    if (FINE) {
      let down = false, sx = 0, sl = 0, moved = false;
      rail.addEventListener('pointerdown', (e) => { down = true; moved = false; sx = e.clientX; sl = rail.scrollLeft; });
      addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - sx;
        if (Math.abs(dx) > 6) { moved = true; rail.classList.add('is-drag'); }
        if (moved) rail.scrollLeft = sl - dx;
      });
      addEventListener('pointerup', () => {
        down = false;
        setTimeout(() => rail.classList.remove('is-drag'), 40);
      });
      rail.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    }
  }));

  /* ---------------- catalog type filter ---------------- */
  safe('filter', () => document.querySelectorAll('[data-filter-root]').forEach((rootEl) => {
    const chips = [...rootEl.querySelectorAll('[data-filter]')];
    const items = [...rootEl.querySelectorAll('[data-ptype]')];
    const empty = rootEl.querySelector('[data-filter-empty]');
    function apply(val) {
      chips.forEach((c) => c.classList.toggle('is-on', c.dataset.filter === val));
      let n = 0;
      items.forEach((it) => {
        const hit = val === 'sve' || it.dataset.ptype === val;
        it.classList.toggle('is-filtered', !hit);
        if (hit) n++;
      });
      if (empty) empty.hidden = n > 0;
    }
    chips.forEach((c) => c.addEventListener('click', () => {
      apply(c.dataset.filter);
      history.replaceState(null, '', c.dataset.filter === 'sve' ? location.pathname : location.pathname + '?tip=' + c.dataset.filter);
    }));
    const q = new URLSearchParams(location.search).get('tip');
    if (q && chips.some((c) => c.dataset.filter === q)) apply(q);
  }));

  /* ---------------- auto-scroll with manual override (lookbook + ocene) ----------------
     Both belts are REAL scroll containers: any swipe/wheel/drag scrolls them
     natively, and the drift resumes 1.4s after the last interaction. The
     driver writes scrollLeft/Top each frame; its own writes are told apart
     from user scrolling by timestamp (scroll events within 90ms of an auto
     write are ours). Content is duplicated once, so wrapping back by one
     sequence length is invisible. */
  function autoScroll(el, axis, speed, wrapLen) {
    const K = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    let down = false, hover = false, lastUser = 0, lastAuto = 0, prev = 0, running = false;
    // fractional position lives HERE: scrollLeft/Top writes are rounded to
    // integers by WebKit, so read-modify-write of sub-pixel steps never moves
    let pos = null;
    const touchUser = () => { lastUser = performance.now(); pos = null; };
    el.addEventListener('pointerdown', () => { down = true; touchUser(); }, { passive: true });
    addEventListener('pointerup', () => { down = false; touchUser(); }, { passive: true });
    addEventListener('pointercancel', () => { down = false; touchUser(); }, { passive: true });
    if (FINE) {
      el.addEventListener('pointerenter', () => { hover = true; });
      el.addEventListener('pointerleave', () => { hover = false; touchUser(); });
    }
    el.addEventListener('wheel', touchUser, { passive: true });
    el.addEventListener('scroll', () => {
      if (performance.now() - lastAuto > 90) touchUser();   // iOS momentum keeps this fresh until the coast ends
    }, { passive: true });
    function tick(now) {
      if (!running) return;
      const dt = Math.min(64, now - (prev || now));
      prev = now;
      if (!down && !hover && !document.hidden && now - lastUser > 1400) {
        const len = wrapLen();
        if (len > 0) {
          if (pos === null) pos = el[K];   // re-sync after user scrolling
          pos += speed * dt / 1000;
          if (pos >= len) pos -= len;
          lastAuto = now;
          el[K] = pos;
        }
      }
      requestAnimationFrame(tick);
    }
    new IntersectionObserver(([en]) => {
      const was = running;
      running = en.isIntersecting;
      if (running && !was) { prev = 0; requestAnimationFrame(tick); }
    }).observe(el);
  }

  safe('lookbook', () => document.querySelectorAll('.look__rail').forEach((rail) => {
    const seq = rail.querySelector('.look__seq');
    if (!seq) return;
    autoScroll(rail, 'x', 45, () => seq.offsetWidth || 0);
    if (FINE) {
      // mouse drag-to-scroll, with click suppression after a real drag
      let sx = 0, sl = 0, moved = false, dragging = false;
      rail.addEventListener('pointerdown', (e) => { dragging = true; moved = false; sx = e.clientX; sl = rail.scrollLeft; });
      addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - sx;
        if (Math.abs(dx) > 6) { moved = true; rail.classList.add('is-drag'); }
        if (moved) rail.scrollLeft = sl - dx;
      });
      addEventListener('pointerup', () => { dragging = false; setTimeout(() => rail.classList.remove('is-drag'), 40); });
      rail.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    }
  }));

  safe('feed', () => document.querySelectorAll('[data-feed]').forEach((box) => {
    const cols = [...box.querySelectorAll('.feed__col')];
    if (!cols.length) return;
    cols.forEach((c) => { c.innerHTML += c.innerHTML; });   // seamless loop
    cols.forEach((c, i) => autoScroll(c, 'y', i % 2 ? 17 : 25, () => {
      const half = c.scrollHeight / 2;
      return half > c.clientHeight ? half : 0;   // too little content to loop
    }));
  }));

  /* ---------------- FAQ accordion ---------------- */
  safe('faq', () => document.querySelectorAll('.faq__item').forEach((item) => {
    const q = item.querySelector('.faq__q');
    q && q.addEventListener('click', () => {
      const open = item.classList.contains('is-open');
      item.parentElement.querySelectorAll('.faq__item.is-open').forEach((o) => {
        o.classList.remove('is-open');
        o.querySelector('.faq__q').setAttribute('aria-expanded', 'false');
      });
      if (!open) { item.classList.add('is-open'); q.setAttribute('aria-expanded', 'true'); }
    });
  }));

  /* ---------------- crew card tilt ---------------- */
  if (FINE && !REDUCED) safe('tilt', () => {
    document.querySelectorAll('.crew__card').forEach((card) => {
      card.setAttribute('data-tilt-live', '');
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - .5;
        const y = (e.clientY - r.top) / r.height - .5;
        card.style.setProperty('--tx', (x * 7) + 'deg');
        card.style.setProperty('--ty', (-y * 7) + 'deg');
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--tx', '0deg');
        card.style.setProperty('--ty', '0deg');
      });
    });
  });

  /* ---------------- orbit stage parallax ---------------- */
  if (FINE && !REDUCED) safe('orbit', () => {
    document.querySelectorAll('.orbit__stage').forEach((stage) => {
      stage.addEventListener('pointermove', (e) => {
        const r = stage.getBoundingClientRect();
        stage.style.setProperty('--px', ((e.clientX - r.left) / r.width - .5).toFixed(3));
        stage.style.setProperty('--py', ((e.clientY - r.top) / r.height - .5).toFixed(3));
      });
      stage.addEventListener('pointerleave', () => {
        stage.style.setProperty('--px', '0');
        stage.style.setProperty('--py', '0');
      });
    });
  });

  /* ---------------- toast ---------------- */
  let toastEl = null, toastT = 0;
  window.MCHtoast = function (html) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = html;
    requestAnimationFrame(() => toastEl.classList.add('is-show'));
    clearTimeout(toastT);
    toastT = setTimeout(() => toastEl.classList.remove('is-show'), 3400);
  };

  /* ---------------- newsletter (footer) ---------------- */
  safe('news', () => document.querySelectorAll('[data-news-form]').forEach((form) => {
    // Shopify customer form posts + redirects; we let it, but flag success via ?customer_posted
    if (new URLSearchParams(location.search).get('customer_posted') === 'true') {
      const note = form.parentElement.querySelector('.ftr__news-note');
      if (note) note.innerHTML = '<span class="ftr__news-ok">✓ Tu si. Javljamo ti prvi kad nešto padne.</span>';
    }
  }));
})();
